import type { PatientReferralType } from 'app/services/metrics'
import { FormEvent, useEffect, useState } from 'react'
import useClientSideTableFilters from 'app/hooks/useClientSideTableFilters'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { notifySuccess } from 'app/services/notify.client'
import { useWorkspace } from 'app/utils'
import { FilterValueType } from 'app/filter.utils'
import { debug, logError } from 'lib/logging'

export type ReferralMappingLoaderData = {
  referralTypes: Promise<{ hits: PatientReferralType[]; total: number }>
  canEditMapping: boolean
}

export const referralFilterableColumns = [
  {
    attribute: `locationIds`,
    displayName: `Locations`,
    type: FilterValueType.id,
    optionsSource: `/api/locations`,
  },
]

export type UseReferralMappingReturn = {
  canEditMapping: boolean
  filteredRows: PatientReferralType[] | undefined
  referralTypes: { hits: PatientReferralType[] } | undefined
  isSaving: boolean
  orgId: string
  onSave: (e: FormEvent) => void
  onSearchValueChange: (value: string) => void
}

export function useReferralMapping(): UseReferralMappingReturn {
  const workspace = useWorkspace()
  const checkPermission = useCheckPermission()
  const canEditMapping = checkPermission([`org:settings:edit-mapping`, `settings:mapping:edit`])
  const [referralTypes, setReferralTypes] = useState<{ hits: PatientReferralType[] }>()
  const [searchValue, setSearchValue] = useState<string>()
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    fetch(`/api/pms-referral-mappings`, { credentials: `include` })
      .then((res) => res.json())
      .then((data) => setReferralTypes(data.referralTypes ?? data))
      .catch((err) => logError(`Error fetching referral mappings`, err))
  }, [])

  const filteredRows = useClientSideTableFilters(
    referralTypes?.hits,
    referralFilterableColumns,
    `referralType`,
    [`referralType`],
    searchValue,
  )

  const onSave = (e: FormEvent) => {
    e.preventDefault()
    const changes = referralTypes!.hits!.filter((r) => r.changed)
    debug(`channel`, `changes`, changes)
    setIsSaving(true)
    fetch(`/api/pms-referral-mappings`, {
      method: `POST`,
      credentials: `include`,
      headers: { 'Content-Type': `application/json` },
      body: JSON.stringify(changes),
    }).then(() => {
      notifySuccess(`Save Successful. It may take a few minutes for your changes to take effect.`)
      setIsSaving(false)
    })
  }

  const onSearchValueChange = (value: string) => setSearchValue(value)

  return {
    canEditMapping,
    filteredRows,
    referralTypes,
    isSaving,
    orgId: workspace!.orgId,
    onSave,
    onSearchValueChange,
  }
}
